/* IL CONFRONTO FRA CANDIDATI.
 *
 * È il motivo per cui i Preferiti esistono: non sono un elenco, sono la
 * rosa dei candidati e prima o poi bisogna sceglierne uno.
 *
 * La cosa che questo pannello deve impedire è il confronto silenzioso fra
 * periodi diversi. Le stesse strategie danno esiti opposti su 2017–2026 e su
 * 2022–2026: due colonne affiancate con i numeri allineati suggeriscono che
 * siano paragonabili, e se i periodi non coincidono non lo sono. Quindi il
 * periodo sta su ogni colonna, e quando differiscono c'è un avvertimento
 * sopra — non un asterisco, non un suggerimento al passaggio del mouse.
 */

import {
  Avvertenza,
  DistintivoVerdetto,
  IconaStrategia,
  NeiRibassi,
  NumeroConSoglia,
  Pulsante,
  TernaMetriche,
  Tenuta,
  divisioneDaMetodo,
} from '../../componenti';
import { num, pct, timeframe } from '../../lib/formato';
import { leggiAPariRischio } from '../../lib/metriche';
import { periodoDiRiga, type PreferitoLeggero } from './dati';

export function Confronto({
  candidati,
  onTogli,
  onSvuota,
  onApri,
}: {
  candidati: PreferitoLeggero[];
  onTogli: (id: number) => void;
  onSvuota: () => void;
  onApri: (id: number) => void;
}) {
  if (candidati.length < 2) return null;

  const periodi = Array.from(new Set(candidati.map(periodoDiRiga)));
  const mercati = Array.from(new Set(candidati.map((c) => `${c.asset}/${c.currency}`)));
  const timeframes = Array.from(new Set(candidati.map((c) => timeframe(c.candleSize))));

  return (
    <div className="sb-pf-confronto">
      <div className="sb-riga">
        <div style={{ flex: 1, minWidth: 160 }}>
          <div className="sb-gr-sezione__titolo">Confronto · {num(candidati.length)} candidati</div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
            ogni colonna porta il proprio periodo di misura
          </div>
        </div>
        <Pulsante variante="tenue" onClick={onSvuota}>
          Svuota il confronto
        </Pulsante>
      </div>

      {periodi.length > 1 && (
        <Avvertenza livello="cautela">
          Periodi di misura diversi: {periodi.join(' e ')}. Le stesse strategie danno esiti opposti
          su intervalli diversi — questi numeri non sono paragonabili fra loro.
        </Avvertenza>
      )}

      {mercati.length > 1 && (
        <Avvertenza livello="cautela">
          Mercati diversi: {mercati.join(' e ')}. Il profitto di due asset differenti dice quanto è
          salito l’asset, non quale strategia è migliore: guarda lo scarto dal mercato.
        </Avvertenza>
      )}

      {periodi.length === 1 && mercati.length === 1 && timeframes.length > 1 && (
        <Avvertenza livello="nota">
          Timeframe diversi: {timeframes.join(' e ')}. Il numero di operazioni non è confrontabile,
          il profitto sì.
        </Avvertenza>
      )}

      <div className="sb-pf-confronto__colonne">
        {candidati.map((c) => {
          const periodo = periodoDiRiga(c);
          return (
            <div key={c.id} className="sb-pf-candidato">
              <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
                <IconaStrategia nome={c.strategyName} dim={22} />
                <span className="sb-pf-candidato__nome" style={{ flex: 1, minWidth: 60 }}>
                  {c.strategyName}
                </span>
                <DistintivoVerdetto profitto={c.strategyChange} mercato={c.marketChange} />
              </div>

              {/* Il periodo non è più qui: lo porta la terna, sotto i suoi tre
                  numeri, dove è il periodo DI quei numeri e non della scheda. */}
              <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
                <span className="sb-nota">
                  {c.asset}/{c.currency} · {timeframe(c.candleSize)}
                </span>
              </div>

              <TernaMetriche
                densita="riquadri"
                profitto={c.strategyChange}
                drawdown={c.drawdownMaxValue}
                operazioni={c.tradesNumber}
                periodo={periodo}
              />

              <div className="sb-pf-candidato__riga">
                <span>Mercato nello stesso periodo</span>
                <b className="sb-tono-neutro">
                  {c.marketChange == null ? 'n/d' : pct(c.marketChange)}
                </b>
              </div>

              <Tenuta
                valore={c.walkForwardScore}
                divisione={divisioneDaMetodo(c.walkForwardMetodo)}
                periodo={periodo}
              />
              <NeiRibassi
                valore={c.bearYearsAvg}
                anniNegativi={c.bearYearsCount}
                periodo={periodo}
              />
              <NumeroConSoglia
                titolo="A pari rischio"
                lettura={leggiAPariRischio(c.strategyChange, c.drawdownMaxValue)}
                periodo={periodo}
              />

              <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
                <Pulsante variante="tenue" onClick={() => onApri(c.id)}>
                  Apri il backtest
                </Pulsante>
                <Pulsante variante="nudo" onClick={() => onTogli(c.id)}>
                  Togli dal confronto
                </Pulsante>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
